// Generated from legacy db.ts to keep files under 500 lines.
import { sql } from "../../base";
import type { Goal } from "../../types";

const goalsModule = {
  async findByUserId(userId: string, includeArchived: boolean = false): Promise<Goal[]> {
    try {
      const result = includeArchived
        ? await sql`
            SELECT * FROM goals
            WHERE user_id = ${userId}
            ORDER BY position ASC, created_at DESC
          `
        : await sql`
            SELECT * FROM goals
            WHERE user_id = ${userId} AND (archived = false OR archived IS NULL)
            ORDER BY position ASC, created_at DESC
          `;
      return (result || []) as Goal[];
    } catch (error) {
      console.error('Error fetching goals:', error);
      return [];
    }
  },
  
  async findArchivedByUserId(userId: string): Promise<Goal[]> {
    try {
      const result = await sql`
        SELECT * FROM goals
        WHERE user_id = ${userId} AND archived = true
        ORDER BY updated_at DESC
      `;
      return (result || []) as Goal[];
    } catch (error) {
      console.error('Error fetching archived goals:', error);
      return [];
    }
  },
  
  async create(data: Omit<Goal, 'id' | 'created_at' | 'updated_at'>): Promise<Goal> {
    const result = await sql`
      INSERT INTO goals (user_id, title, description, category, priority, year, position, matter_id, project_id, client_id, start_date, deadline)
      VALUES (${data.user_id}, ${data.title}, ${data.description}, ${data.category}, 
              ${data.priority}, ${data.year}, ${data.position}, ${data.matter_id}, ${data.project_id}, ${data.client_id},
              ${data.start_date}, ${data.deadline})
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async update(id: string, userId: string, data: Partial<Goal>): Promise<Goal> {
    const result = await sql`
      UPDATE goals
      SET title = COALESCE(${data.title}, title),
          description = COALESCE(${data.description}, description),
          category = COALESCE(${data.category}, category),
          priority = COALESCE(${data.priority}, priority),
          year = COALESCE(${data.year}, year),
          position = COALESCE(${data.position}, position),
          matter_id = CASE WHEN ${data.matter_id === undefined} THEN matter_id ELSE ${data.matter_id} END,
          project_id = CASE WHEN ${data.project_id === undefined} THEN project_id ELSE ${data.project_id} END,
          client_id = CASE WHEN ${data.client_id === undefined} THEN client_id ELSE ${data.client_id} END,
          start_date = CASE WHEN ${data.start_date === undefined} THEN start_date ELSE ${data.start_date} END,
          deadline = CASE WHEN ${data.deadline === undefined} THEN deadline ELSE ${data.deadline} END,
          archived = COALESCE(${data.archived}, archived),
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async archive(id: string, userId: string): Promise<Goal> {
    const result = await sql`
      UPDATE goals
      SET archived = true,
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async unarchive(id: string, userId: string): Promise<Goal> {
    const result = await sql`
      UPDATE goals
      SET archived = false,
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async markComplete(id: string, userId: string): Promise<Goal> {
    const result = await sql`
      UPDATE goals
      SET is_complete = true,
          completed_at = now(),
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async markIncomplete(id: string, userId: string): Promise<Goal> {
    const result = await sql`
      UPDATE goals
      SET is_complete = false,
          completed_at = NULL,
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Goal;
  },
  
  async delete(id: string, userId: string): Promise<boolean> {
    try {
      const result = await sql`
        DELETE FROM goals
        WHERE id = ${id} AND user_id = ${userId}
        RETURNING id
      `;
      // Check if any rows were deleted by checking the returned array
      return result && result.length > 0;
    } catch (error) {
      console.error('Error deleting goal:', error);
      return false;
    }
  }
};

export type GoalsModule = typeof goalsModule;

export function registerGoals(db: { goals?: GoalsModule }) {
  db.goals = goalsModule;
}

export { goalsModule as goals };
